import React, { useState, useCallback, useEffect } from 'react';
import { useLayout } from '../../../context/LayoutContext';
import { Link } from 'react-router-dom';
import { 
  useOperationsHierarchy, 
  useMultipleVehicleCounts 
} from "../../../hooks/useDocumentationVehicleQuery";
import IconVehicle from '../../../assets/svg/layout/car.svg';
import ClockIcon from '../../../assets/svg/layout/clock.svg';
import LocationIcon from '../../../assets/svg/layout/location_report.svg';
import ReportIcon from '../../../assets/svg/layout/location_report.svg';
import '../../../assets/scss/views/DashboardOperations.scss';
import LoadingAnimation from '../../../components/loading/LoadingAnimation';

const DashboardOperations = () => {
  const { theme } = useLayout();
  const [expandedOperations, setExpandedOperations] = useState([]);
  const [hoveredOperation, setHoveredOperation] = useState(null);
  
  // Hook para operaciones con mejor manejo de estados
  const { 
    operations, 
    isLoading: isLoadingOperations, 
    isError: isOperationsError,
    error: operationsError 
  } = useOperationsHierarchy({
    role: 'log'
  });
  
  // Validar que operations sea un array válido
  const validOperations = React.useMemo(() => {
    return Array.isArray(operations) ? operations : [];
  }, [operations]);
  
  // Obtener IDs solo de operaciones válidas
  const operationIds = React.useMemo(() => {
    return validOperations.map(op => op.id).filter(Boolean);
  }, [validOperations]);
  
  // Hook de conteos con validación
  const {
    vehicleCounts = {},
    isLoading: isLoadingCounts,
    isError: isCountError,
  } = useMultipleVehicleCounts(operationIds, {
    includeSuboperations: true,
    includeInactive: false,
    enabled: operationIds.length > 0
  });

  // Función memoizada para obtener logos
  const getLogo = useCallback((operationName, parentName = '') => {
    if (!operationName) return '';
    
    let logoPath = '';
    
    try {
      if (parentName === 'Terpel') {
        if (operationName === 'Norte') {
          logoPath = require('../../../assets/img/logos/terpel-norte.svg').default;
        } else if (operationName === 'Sur') {
          logoPath = require('../../../assets/img/logos/terpel-sur.svg').default;
        } else {
          logoPath = require('../../../assets/img/logos/terpel.svg').default;
        }
      } 
      else if (parentName === 'Impala') {
        if (operationName === 'Emerald' || operationName === 'Emerald Energy') {
          logoPath = require('../../../assets/img/logos/emerald.png');
        } else if (operationName === 'Gran Tierra Energy') {
          logoPath = require('../../../assets/img/logos/grantierra.png');
        } else {
          logoPath = require('../../../assets/img/logos/impala.png');
        }
      }
      else if (operationName === 'Ecopetrol') {
        logoPath = require('../../../assets/img/logos/ecopetrol.svg').default;
      }
      else if (operationName === 'Impala') {
        logoPath = require('../../../assets/img/logos/impala.png');
      }
      else if (operationName === 'Terpel') {
        logoPath = require('../../../assets/img/logos/terpel.svg').default;
      }
      else {
        logoPath = require('../../../assets/img/Logo_base.png');
      }
    } catch (error) {
      console.warn(`⚠️ Logo no encontrado para: ${operationName}`, error);
      logoPath = '';
    }
    
    return logoPath;
  }, []);
  
  // Procesar operaciones con validación robusta
  const operationsWithVehicleCounts = React.useMemo(() => {
    if (!validOperations.length) return [];
    
    return validOperations.map(operation => {
      const vehicleCount = vehicleCounts[operation.id] || 0;
      const processedDivisions = Array.isArray(operation.divisions) 
        ? operation.divisions.map(division => ({
            ...division,
            logo: getLogo(division.name, operation.name),
            route: division.route || `/scheduler/${division.id}`,
            vehicleCount: vehicleCounts[division.id] || 0
          }))
        : [];

      return {
        ...operation,
        logo: getLogo(operation.name),
        vehicles: vehicleCount,
        route: operation.route || `/scheduler/${operation.id}`,
        divisions: processedDivisions
      };
    });
  }, [validOperations, vehicleCounts, getLogo]);

  // Función para toggle de operaciones expandidas
  const toggleOperation = useCallback((opId) => {
    setExpandedOperations(prev => 
      prev.includes(opId) 
        ? prev.filter(id => id !== opId)
        : [...prev, opId]
    );
  }, []);
  
  // Handlers para hover en operaciones
  const handleOperationMouseEnter = useCallback((operation) => {
    setHoveredOperation(operation);
  }, []);

  const handleOperationMouseLeave = useCallback(() => {
    setHoveredOperation(null);
  }, []);
  
  // Manejador para acciones del panel
  const handlePanelAction = useCallback((actionId, operation) => {
    if (!operation?.id) return;
    
    switch (actionId) {
      case 'vehicles':
        console.log('🚗 Gestión de vehículos para:', operation.name);
        break;
      case 'schedule':
        window.location.href = operation.route || `/scheduler/${operation.id}`;
        break;
      case 'monitoring':
        console.log('📊 Monitoreo para:', operation.name);
        break;
      case 'reports':
        console.log('📈 Reportes para:', operation.name);
        break;
      default:
        console.warn('⚠️ Acción no reconocida:', actionId);
        break;
    }
  }, []);
  
  // Función para obtener detalles de operación
  const getOperationDetails = useCallback((operation) => {
    if (!operation?.name) return null;
    
    const operationData = {
      'Impala': {
        description: 'Operación de transporte especializado en combustibles y químicos',
        services: ['Transporte de combustibles', 'Logística especializada', 'Mantenimiento de flota'],
        coverage: ['Región Andina', 'Costa Atlántica', 'Orinoquia'],
        stats: {
          activeVehicles: operation?.vehicles || 0,
          dailyOperations: 45,
          coverage: '85%',
          efficiency: '94%'
        }
      },
      'Terpel': {
        description: 'Red de distribución de combustibles y servicios energéticos',
        services: ['Distribución combustibles', 'Estaciones de servicio', 'Lubricantes'],
        coverage: ['Norte', 'Sur', 'Región Central'],
        stats: {
          activeVehicles: operation?.vehicles || 0,
          dailyOperations: 120,
          coverage: '92%',
          efficiency: '96%'
        }
      },
      'Ecopetrol': {
        description: 'Empresa integrada de petróleo y gas con operaciones de refinación',
        services: ['Refinación', 'Petroquímica', 'Exploración'],
        coverage: ['Nacional', 'Internacional'],
        stats: {
          activeVehicles: operation?.vehicles || 0,
          dailyOperations: 200,
          coverage: '98%',
          efficiency: '97%'
        }
      }
    };

    return operationData[operation.name] || operationData['Impala'];
  }, []);
  
  // Botones de acción para el panel
  const actionButtons = React.useMemo(() => [
    {
      id: 'vehicles',
      label: 'Gestión de Vehículos',
      icon: IconVehicle,
      description: 'Administrar flota y asignaciones',
      color: 'primary'
    },
    {
      id: 'schedule',
      label: 'Programación',
      icon: ClockIcon,
      description: 'Planificar rutas y horarios',
      color: 'secondary'
    },
    {
      id: 'monitoring',
      label: 'Monitoreo',
      icon: LocationIcon,
      description: 'Seguimiento en tiempo real',
      color: 'success'
    },
    {
      id: 'reports',
      label: 'Reportes',
      icon: ReportIcon,
      description: 'Análisis y métricas',
      color: 'info'
    }
  ], []);
  
  // Lógica de estados más granular
  const isLoading = isLoadingOperations || (operationIds.length > 0 && isLoadingCounts);
  const hasError = isOperationsError && !validOperations.length;
  const hasPartialError = isCountError && !isOperationsError;
  
  // Renderizado condicional
  if (isLoading) {
    return <LoadingAnimation />;
  }
  
  if (hasError) {
    return (
      <div className={`GM__${theme}-error-container`}>
        <div className={`GM__${theme}-error-message`}>
          <h3>Error al cargar operaciones</h3>
          <p>No se pudieron cargar las operaciones. Por favor, intenta nuevamente.</p>
          {operationsError && (
            <details className={`GM__${theme}-error-details`}>
              <summary>Detalles del error</summary>
              <pre>{JSON.stringify(operationsError, null, 2)}</pre>
            </details>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className={`GM__${theme}-dashboard-operations`}>
      <div className={`GM__${theme}-main-content ${expandedOperations.length > 0 ? 'has-expanded' : ''}`}>
        <div className={`GM__${theme}-operations-container`}>
          {operationsWithVehicleCounts.length > 0 ? (
            operationsWithVehicleCounts.map(operation => (
              <div key={operation.id} className={`GM__${theme}-operation-wrapper`}>
                {/* Tarjeta circular con hover handlers */}
                <div 
                  className={`GM__${theme}-operation-card-container ${expandedOperations.includes(operation.id) ? 'active' : ''}`}
                  onClick={() => toggleOperation(operation.id)}
                  onMouseEnter={() => handleOperationMouseEnter(operation)}
                  onMouseLeave={handleOperationMouseLeave}
                >
                  <div className={`GM__${theme}-operation-card ${expandedOperations.includes(operation.id) ? 'active' : ''}`}>
                    {operation.logo ? (
                      <img 
                        src={operation.logo} 
                        alt={operation.name} 
                        className={`GM__${theme}-operation-logo`}
                        onError={(e) => {
                          console.warn(`❌ Error cargando logo para ${operation.name}`);
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className={`GM__${theme}-operation-logo-placeholder`}>
                        {operation.name.substring(0, 2).toUpperCase()}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Contenido expandido */}
                {expandedOperations.includes(operation.id) && (
                  <div className={`GM__${theme}-operation-expanded`}>
                    {/* Card de vehículos */}
                    <div className={`GM__${theme}-vehicle-card`}>
                      <img src={IconVehicle} alt="Vehículos" className={`GM__${theme}-vehicle-icon`} />
                      <span className={`GM__${theme}-vehicle-label`}>Vehículos:</span>
                      <span className={`GM__${theme}-vehicle-count`}>
                        {operation.vehicles}
                      </span>
                    </div>
                    
                    {/* Operación principal como enlace */}
                    <Link 
                      to={operation.route} 
                      className={`GM__${theme}-division-card main-operation`}
                    >
                      {operation.logo ? (
                        <img 
                          src={operation.logo} 
                          alt={operation.name} 
                          className={`GM__${theme}-division-logo`}
                        />
                      ) : (
                        <div className={`GM__${theme}-division-logo-placeholder`}>
                          {operation.name.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                    </Link>
                    
                    {/* Divisiones como enlaces */}
                    {operation.divisions?.length > 0 && (
                      operation.divisions.map((division, index) => (
                        <Link 
                          key={division.id} 
                          to={division.route} 
                          className={`GM__${theme}-division-card`}
                        >
                          {division.logo ? (
                            <img 
                              src={division.logo} 
                              alt={division.name} 
                              className={`GM__${theme}-division-logo`}
                            />
                          ) : (
                            <div className={`GM__${theme}-division-logo-placeholder`}>
                              {division.name.substring(0, 2).toUpperCase()}
                            </div>
                          )}
                          {index < operation.divisions.length - 1 && (
                            <div className={`GM__${theme}-division-divider`}></div>
                          )}
                        </Link>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className={`GM__${theme}-no-operations-message`}>
              <h3>No hay operaciones disponibles</h3>
              <p>No se encontraron operaciones para mostrar en este momento.</p>
            </div>
          )}
        </div>
        
        {/* Panel de información basado en hover */}
        <div className={`GM__${theme}-info-panel-container`}>
          {!hoveredOperation ? (
            <div className={`GM__${theme}-operation-info-panel default`}>
              {/* NUEVO: Banner de advertencia en estado por defecto */}
              {hasPartialError && (
                <div className={`GM__${theme}-warning-banner`}>
                  <div className={`GM__${theme}-warning-icon`}>⚠️</div>
                  <div className={`GM__${theme}-warning-content`}>
                    <span className={`GM__${theme}-warning-title`}>Atención</span>
                    <span className={`GM__${theme}-warning-message`}>
                      Los conteos de vehículos podrían no estar actualizados
                    </span>
                  </div>
                </div>
              )}
              
              <div className={`GM__${theme}-welcome-content`}>
                <h2>Gestión de Operaciones</h2>
                <p>Selecciona una operación para ver información detallada y acceder a las herramientas de gestión.</p>
                <div className={`GM__${theme}-feature-highlights`}>
                  <div className={`GM__${theme}-feature-item`}>
                    <img src={IconVehicle} alt="Vehículos" />
                    <span>Gestión de Flota</span>
                  </div>
                  <div className={`GM__${theme}-feature-item`}>
                    <img src={ClockIcon} alt="Programación" />
                    <span>Programación Inteligente</span>
                  </div>
                  <div className={`GM__${theme}-feature-item`}>
                    <img src={LocationIcon} alt="Monitoreo" />
                    <span>Monitoreo en Tiempo Real</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={`GM__${theme}-operation-info-panel active hover-active`}>
              {/* NUEVO: Banner de advertencia en estado activo */}
              {hasPartialError && (
                <div className={`GM__${theme}-warning-banner compact`}>
                  <div className={`GM__${theme}-warning-icon`}>⚠️</div>
                  <span className={`GM__${theme}-warning-message`}>
                    Los conteos de vehículos podrían no estar actualizados
                  </span>
                </div>
              )}
              
              {/* Header con información de la operación */}
              <div className={`GM__${theme}-operation-header`}>
                <div className={`GM__${theme}-operation-title`}>
                  <h2>{hoveredOperation.name}</h2>
                  <p>{getOperationDetails(hoveredOperation)?.description}</p>
                </div>
                {hoveredOperation.logo && (
                  <img 
                    src={hoveredOperation.logo} 
                    alt={hoveredOperation.name}
                    className={`GM__${theme}-operation-logo-large`}
                  />
                )}
              </div>

              {/* Estadísticas clave */}
              <div className={`GM__${theme}-stats-grid`}>
                <div className={`GM__${theme}-stat-card`}>
                  <span className={`GM__${theme}-stat-value`}>
                    {getOperationDetails(hoveredOperation)?.stats.activeVehicles}
                  </span>
                  <span className={`GM__${theme}-stat-label`}>Vehículos Activos</span>
                </div>
                <div className={`GM__${theme}-stat-card`}>
                  <span className={`GM__${theme}-stat-value`}>
                    {getOperationDetails(hoveredOperation)?.stats.dailyOperations}
                  </span>
                  <span className={`GM__${theme}-stat-label`}>Operaciones Diarias</span>
                </div>
                <div className={`GM__${theme}-stat-card`}>
                  <span className={`GM__${theme}-stat-value`}>
                    {getOperationDetails(hoveredOperation)?.stats.coverage}
                  </span>
                  <span className={`GM__${theme}-stat-label`}>Cobertura</span>
                </div>
                <div className={`GM__${theme}-stat-card`}>
                  <span className={`GM__${theme}-stat-value`}>
                    {getOperationDetails(hoveredOperation)?.stats.efficiency}
                  </span>
                  <span className={`GM__${theme}-stat-label`}>Eficiencia</span>
                </div>
              </div>

              {/* Servicios */}
              <div className={`GM__${theme}-services-section`}>
                <h3>Servicios</h3>
                <div className={`GM__${theme}-services-list`}>
                  {getOperationDetails(hoveredOperation)?.services.map((service, index) => (
                    <span key={index} className={`GM__${theme}-service-tag`}>
                      {service}
                    </span>
                  ))}
                </div>
              </div>

              {/* Botones de acción */}
              <div className={`GM__${theme}-actions-grid`}>
                {actionButtons.map((action) => (
                  <button
                    key={action.id}
                    className={`GM__${theme}-action-button ${action.color}`}
                    onClick={() => handlePanelAction(action.id, hoveredOperation)}
                  >
                    <img src={action.icon} alt={action.label} />
                    <div className={`GM__${theme}-action-content`}>
                      <span className={`GM__${theme}-action-label`}>{action.label}</span>
                      <span className={`GM__${theme}-action-description`}>{action.description}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardOperations;